import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiMenu, FiX, FiSearch, FiUser, FiHeart, FiLogOut, FiFilm } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';

const Navbar: React.FC = () => {
  const { user, signInWithOtp, signOut } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [authError, setAuthError] = useState('');
  const [authSuccess, setAuthSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setAuthSuccess('');
    setIsLoading(true);

    const { error } = await signInWithOtp(email);

    if (error) {
      setAuthError(error.message);
      setIsLoading(false);
    } else {
      setIsLoading(false);
      setEmail('');
      setAuthSuccess('Check your email for the magic link!');
    }
  };

  const handleSignOut = async () => {
    await signOut();
    setIsUserMenuOpen(false);
    navigate('/');
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50">
      <div className="bg-white/70 backdrop-blur-sm shadow-md">
        <div className="container-custom">
          <div className="flex justify-between items-center h-16 md:h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img src="/Logo-transparent.png" alt="Next in Cinema Logo" className="h-8 mr-2" />
              <span className="text-xl font-heading font-bold text-secondary-dark self-center mt-[-2px]">
                Next in Cinema
              </span>
            </Link>

            {/* Navigation Links */}
            <div className="hidden md:flex items-center space-x-6">
              <Link
                to="/archive"
                className="text-secondary-dark hover:text-blue-600 transition-colors duration-200 font-medium"
              >
                Movie Archive
              </Link>
              <Link
                to="/trailers"
                className="flex items-center gap-1 text-secondary-dark hover:text-blue-600 transition-colors duration-200 font-medium"
              >
                <FiFilm className="w-4 h-4" />
                Trailers
              </Link>
              
              {user && (
                <>
                  <Link
                    to="/watchlist"
                    className="flex items-center gap-1 text-secondary-dark hover:text-blue-600 transition-colors duration-200 font-medium"
                  >
                    <FiHeart className="w-4 h-4" />
                    My Watchlist
                  </Link>
                  <Link
                    to="/profile"
                    className="text-secondary-dark hover:text-blue-600 transition-colors duration-200 font-medium"
                  >
                    Profile
                  </Link>
                </>
              )}
              
              {/* Authentication */}
              <div className="flex items-center space-x-4">
                {!user ? (
                  <button
                    onClick={() => setIsAuthModalOpen(true)}
                    className="flex items-center space-x-2 bg-primary hover:bg-primary-dark text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
                  >
                    <FiUser className="w-4 h-4" />
                    <span>Sign In</span>
                  </button>
                ) : (
                  <div className="relative" ref={userMenuRef}>
                    <button
                      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                      className="flex items-center space-x-2"
                    >
                      <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white font-medium">
                        {user.email?.charAt(0).toUpperCase()}
                      </div>
                    </button>
                    {isUserMenuOpen && (
                      <div className="absolute right-0 mt-2 w-48 bg-white shadow-lg border border-gray-200 rounded-lg py-2">
                        <div className="px-4 py-2 border-b border-gray-100">
                          <p className="text-sm text-gray-600 truncate">{user.email}</p>
                        </div>
                        <Link
                          to="/profile"
                          className="block px-4 py-2 text-secondary-dark hover:bg-background-light"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          Profile
                        </Link>
                        <Link
                          to="/watchlist"
                          className="block px-4 py-2 text-secondary-dark hover:bg-background-light"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          My Watchlist
                        </Link>
                        <button
                          onClick={handleSignOut}
                          className="flex items-center gap-2 w-full px-4 py-2 text-red-600 hover:bg-background-light"
                        >
                          <FiLogOut className="w-4 h-4" />
                          Sign Out
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-background-light transition-colors duration-200"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? (
                <FiX className="w-6 h-6 text-secondary-dark" />
              ) : (
                <FiMenu className="w-6 h-6 text-secondary-dark" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden border-t border-secondary/10 bg-white/90 backdrop-blur-sm">
              <div className="py-4 space-y-2">
                <Link
                  to="/archive"
                  className="block px-4 py-2 text-secondary-dark hover:text-blue-600 hover:bg-background-light transition-colors duration-200 font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Movie Archive
                </Link>
                <Link
                  to="/trailers"
                  className="flex items-center gap-2 px-4 py-2 text-secondary-dark hover:text-blue-600 hover:bg-background-light transition-colors duration-200 font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <FiFilm className="w-5 h-5" />
                  Trailers
                </Link>
                
                {user && (
                  <>
                    <Link
                      to="/watchlist"
                      className="flex items-center gap-2 px-4 py-2 text-secondary-dark hover:text-blue-600 hover:bg-background-light transition-colors duration-200 font-medium"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <FiHeart className="w-5 h-5" />
                      My Watchlist
                    </Link>
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-secondary-dark hover:text-blue-600 hover:bg-background-light transition-colors duration-200 font-medium"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Profile
                    </Link>
                  </>
                )}
                
                {/* Mobile Authentication */}
                <div className="px-4 py-2">
                  {!user ? (
                    <button
                      className="flex items-center space-x-2 bg-primary hover:bg-primary-dark text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 w-full justify-center"
                      onClick={() => {
                        setIsAuthModalOpen(true);
                        setIsMenuOpen(false);
                      }}
                    >
                      <FiUser className="w-4 h-4" />
                      <span>Sign In</span>
                    </button>
                  ) : (
                    <div className="flex items-center justify-center">
                      <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white font-medium text-lg">
                        {user.email?.charAt(0).toUpperCase()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>

    {/* Auth Modal */}
    {isAuthModalOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-secondary-dark">
              {authMode === 'signin' ? 'Sign In' : 'Create Account'}
            </h2>
            <button
              onClick={() => {
                  setIsAuthModalOpen(false);
                  setAuthError('');
                  setAuthSuccess('');
                }}
              className="text-gray-400 hover:text-gray-600"
            >
              <FiX className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleAuth}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                required
              />
            </div>
            {authSuccess && (
              <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-lg text-sm">
                {authSuccess}
              </div>
            )}
            {authError && (
              <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                {authError}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary hover:bg-primary-dark text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50"
            >
              {isLoading ? 'Sending...' : authMode === 'signin' ? 'Send Magic Link' : 'Send Verification Email'}
            </button>
          </form>

          <div className="mt-4 text-center">
            <button
              onClick={() => {
                setAuthMode(authMode === 'signin' ? 'signup' : 'signin');
                setAuthError('');
                setAuthSuccess('');
              }}
              className="text-primary hover:text-primary-dark text-sm"
            >
              {authMode === 'signin'
                ? "Don't have an account? Sign up"
                : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default Navbar;